import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { appPathname } from '../clubhouse';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const refreshAttempted = useRef(false);

  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh || refreshAttempted.current) return null;
    
    refreshAttempted.current = true;
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refresh });
      const { access_token, refresh_token: newRefresh } = response.data;
      
      localStorage.setItem('access_token', access_token);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      setToken(access_token);
      refreshAttempted.current = false;
      return access_token;
    } catch (error) {
      console.error('Erreur refresh token:', error);
      refreshAttempted.current = false;
      return null;
    }
  }, []);

  const fetchUser = useCallback(async (currentToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken || token}` }
      });
      setUser(response.data);
      return true;
    } catch (error) {
      // Si erreur 401, essayer de rafraîchir le token
      if (error.response?.status === 401) {
        const newToken = await refreshToken();
        if (newToken) {
          // Réessayer avec le nouveau token
          try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            });
            setUser(response.data);
            return true;
          } catch (retryError) {
            console.error('Erreur après refresh:', retryError);
          }
        }
      }
      // Ne pas déconnecter pour les erreurs réseau temporaires
      if (!error.response) {
        console.warn('Erreur réseau, conservation de la session');
        return false;
      }
      return false;
    }
  }, [token, refreshToken]);

  useEffect(() => {
    const isClientPortal = appPathname().startsWith('/client') || appPathname().startsWith('/portal');
    
    if (token && !isClientPortal) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, refresh_token } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);
      refreshAttempted.current = false;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token, refreshUser, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};
